import { z } from 'zod'
import { ORDER_CHANNELS, ORDER_STATUSES, ORDER_TYPES, PAYMENT_METHODS } from './constants'

/**
 * FR-RT-2 — the realtime contract between the API worker, the BranchRoom
 * Durable Object and every screen in the restaurant.
 *
 * Versioning rule (ENGINEERING-PLAN §1): `v` is bumped only for breaking
 * changes; new optional fields are additive and do not bump it. Clients must
 * ignore unknown event types rather than crashing, so a new event can ship
 * before every kiosk has reloaded.
 */
export const EVENT_CONTRACT_VERSION = 1

export const EVENT_TYPES = [
  'ORDER_CREATED',
  'ORDER_UPDATED',
  'ORDER_ACCEPTED',
  'ORDER_PREPARING',
  'ORDER_READY',
  'ORDER_DELIVERED',
  'PAYMENT_COMPLETED',
  'INVENTORY_UPDATED',
] as const
export type EventType = (typeof EVENT_TYPES)[number]

/** Who may receive an event. FR-RT-6: the TV never sees customer PII. */
export const CLIENT_ROLES = ['kds', 'pos', 'tv', 'admin', 'customer'] as const
export type ClientRole = (typeof CLIENT_ROLES)[number]

const orderSummarySchema = z.object({
  id: z.string(),
  orderNumber: z.string(),
  status: z.enum(ORDER_STATUSES),
  channel: z.enum(ORDER_CHANNELS),
  type: z.enum(ORDER_TYPES),
  tableLabel: z.string().nullable().optional(),
  total: z.number().int(),
  isPaid: z.boolean(),
  isPriority: z.boolean().default(false),
  placedAt: z.number().int(),
  prepSeconds: z.number().int().nullable().optional(),
  itemCount: z.number().int(),
})
export type OrderSummary = z.infer<typeof orderSummarySchema>

const envelope = <T extends EventType, S extends z.ZodTypeAny>(type: T, payload: S) =>
  z.object({
    v: z.literal(EVENT_CONTRACT_VERSION),
    type: z.literal(type),
    /** Server timestamp (unix ms) — clients order events by this, not arrival. */
    at: z.number().int(),
    branchId: z.string(),
    /** Correlates an event back to the API request that caused it. */
    requestId: z.string().optional(),
    payload,
  })

export const orderCreatedEvent = envelope('ORDER_CREATED', z.object({ order: orderSummarySchema }))
export const orderUpdatedEvent = envelope('ORDER_UPDATED', z.object({ order: orderSummarySchema }))

const statusChangePayload = z.object({
  orderId: z.string(),
  orderNumber: z.string(),
  from: z.enum(ORDER_STATUSES),
  to: z.enum(ORDER_STATUSES),
  order: orderSummarySchema.optional(),
})

export const orderAcceptedEvent = envelope('ORDER_ACCEPTED', statusChangePayload)
export const orderPreparingEvent = envelope('ORDER_PREPARING', statusChangePayload)
export const orderReadyEvent = envelope('ORDER_READY', statusChangePayload)
export const orderDeliveredEvent = envelope('ORDER_DELIVERED', statusChangePayload)

export const paymentCompletedEvent = envelope(
  'PAYMENT_COMPLETED',
  z.object({
    orderId: z.string(),
    orderNumber: z.string(),
    method: z.enum(PAYMENT_METHODS),
    amount: z.number().int(),
    order: orderSummarySchema.optional(),
  }),
)

export const inventoryUpdatedEvent = envelope(
  'INVENTORY_UPDATED',
  z.object({
    changes: z.array(
      z.object({
        ingredientId: z.string(),
        name: z.string(),
        stock: z.number(),
        isLow: z.boolean(),
      }),
    ),
  }),
)

export const realtimeEventSchema = z.discriminatedUnion('type', [
  orderCreatedEvent,
  orderUpdatedEvent,
  orderAcceptedEvent,
  orderPreparingEvent,
  orderReadyEvent,
  orderDeliveredEvent,
  paymentCompletedEvent,
  inventoryUpdatedEvent,
])

export type RealtimeEvent = z.infer<typeof realtimeEventSchema>

/** Control frames are not domain events; they keep the socket alive (FR-RT-4). */
export const clientMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ping') }),
  z.object({ type: z.literal('subscribe'), orderId: z.string().optional() }),
])
export type ClientMessage = z.infer<typeof clientMessageSchema>

export const serverControlSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('pong'), at: z.number().int() }),
  z.object({
    type: z.literal('hello'),
    at: z.number().int(),
    branchId: z.string(),
    role: z.enum(CLIENT_ROLES),
    v: z.literal(EVENT_CONTRACT_VERSION),
  }),
  z.object({ type: z.literal('error'), code: z.string(), message: z.string() }),
])
export type ServerControl = z.infer<typeof serverControlSchema>

/**
 * FR-RT-6 — which roles receive which events. The TV and customer screens are
 * deliberately starved of anything that is not on the public board.
 */
const AUDIENCE: Record<EventType, readonly ClientRole[]> = {
  ORDER_CREATED: ['kds', 'pos', 'admin'],
  ORDER_UPDATED: ['kds', 'pos', 'admin'],
  ORDER_ACCEPTED: ['kds', 'pos', 'admin', 'customer'],
  ORDER_PREPARING: ['kds', 'pos', 'admin', 'tv', 'customer'],
  ORDER_READY: ['kds', 'pos', 'admin', 'tv', 'customer'],
  ORDER_DELIVERED: ['kds', 'pos', 'admin', 'tv', 'customer'],
  PAYMENT_COMPLETED: ['kds', 'pos', 'admin'],
  INVENTORY_UPDATED: ['admin'],
}

export function isEventVisibleTo(type: EventType, role: ClientRole): boolean {
  return AUDIENCE[type].includes(role)
}

/** Public surfaces get order numbers and status only — never names or totals. */
export function redactForRole(event: RealtimeEvent, role: ClientRole): RealtimeEvent {
  if (role !== 'tv' && role !== 'customer') return event

  if ('order' in event.payload && event.payload.order) {
    const { orderNumber, status, id, channel, type, placedAt, isPriority } = event.payload.order
    return {
      ...event,
      payload: {
        ...event.payload,
        order: {
          id,
          orderNumber,
          status,
          channel,
          type,
          placedAt,
          isPriority,
          total: 0,
          isPaid: false,
          itemCount: 0,
        },
      },
    } as RealtimeEvent
  }
  return event
}

export function parseRealtimeEvent(raw: unknown): RealtimeEvent | null {
  const result = realtimeEventSchema.safeParse(raw)
  return result.success ? result.data : null
}
